import { useCallback, useEffect, useRef, useState } from 'react';

import { preloadAllAssets } from '@/utils/assetManifest';

export type AssetPreloadStatus = 'loading' | 'ready' | 'offline';

interface UseAssetPreloadReturn {
  status: AssetPreloadStatus;
  retry: () => void;
}

/**
 * Preloads all game assets on mount so gameplay + result screens work offline.
 * Returns a status that gates the UI: the game shell should only render once
 * status === "ready".
 */
export function useAssetPreload(): UseAssetPreloadReturn {
  const [status, setStatus] = useState<AssetPreloadStatus>('loading');
  const settledRef = useRef(false);

  useEffect(() => {
    if (!window.navigator.onLine) {
      setStatus('offline');
      return;
    }

    settledRef.current = false;

    const handleOffline = () => {
      if (settledRef.current) return;
      settledRef.current = true;
      setStatus('offline');
    };
    window.addEventListener('offline', handleOffline);

    preloadAllAssets()
      .then(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        setStatus('ready');
      })
      .catch((err: unknown) => {
        console.error('Preload failed:', err);
        if (settledRef.current) return;
        settledRef.current = true;
        setStatus('offline');
      });

    return () => {
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const retry = useCallback(() => {
    window.location.reload();
  }, []);

  return { status, retry };
}
